import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  CircularProgress,
  Autocomplete,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  PlayArrow as PlayIcon,
  Description as DescriptionIcon,
  Architecture as ArchitectureIcon,
  Code as CodeIcon,
  Task as TaskIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  Chat as ChatIcon,
  Forum as ForumIcon,
  ContentCopy as CopyIcon,
  ManageAccounts as ManageAccountsIcon,
  GroupAdd as GroupAddIcon,
  RemoveCircleOutline as RemoveCircleIcon,
} from '@mui/icons-material';
import { ApiService } from '../../../services/api';
import { SpecKitDockerService } from '../../../services/SpecKitDockerService';
import { SDDPhase, PhaseStatus, ProjectSDDState } from '../../../types/SDDTypes';
import { useNotifications } from '../../ui/NotificationSystem';
import { DiscoveryInterviewChat } from '../discovery';
import ProjectAssignmentService, { Developer as AssignmentDeveloper } from '../../../services/ProjectAssignmentService';
import GitHubImport from '../GitHubImport';

interface Project {
  id: number;
  name: string;
  description?: string;
  status: string;
  priority: string;
  client?: {
    id: number;
    name: string;
    email: string;
  };
  projectManager?: {
    id: number;
    name: string;
    email: string;
  };
  developers?: Array<{
    id: number;
    name: string;
    email: string;
    pivot?: {
      role?: string;
      status?: string;
      assigned_at?: string;
      assigned_hours?: number;
      allocation_percentage?: number;
    };
  }>;
  expected_start_date?: string;
  expected_end_date?: string;
  completion_percentage?: number;
  budget_info?: {
    amount: number;
    hourly_rate: number;
    billable_hours: number;
  };
  project_metadata?: {
    sdd_workflow_enabled?: boolean;
    sdd_state?: ProjectSDDState;
    sdd_outputs?: {
      specification?: any;
      plan?: any;
      tasks?: any;
    };
    speckit_integration_enabled?: boolean;
    client_guided_workflow?: boolean;
    require_client_approval?: boolean;
    github_repository?: {
      url: string;
      full_name: string;
      created_at: string;
    };
  };
  created_at: string;
  updated_at: string;
}

interface DiscoveryTranscriptMessage {
  role: 'ai' | 'client';
  message: string;
  question_id?: number;
  timestamp: string;
}

interface DiscoveryQuestion {
  id: number;
  question: string;
  answer?: string;
  answered?: boolean;
  answered_at?: string;
  context?: string;
  category?: string;
}

interface DiscoveryInterviewPayload {
  interview: {
    id: number;
    project_id: number;
    status: 'in_progress' | 'completed' | 'abandoned';
    initial_idea: string;
    questions_asked: number;
    questions_answered: number;
    progress_percentage: number;
    started_at: string;
    completed_at?: string;
  };
  questions: DiscoveryQuestion[];
  transcript: DiscoveryTranscriptMessage[];
  answered_questions: DiscoveryQuestion[];
  unanswered_questions: DiscoveryQuestion[];
}

interface AssignableUser {
  id: number;
  name: string;
  email: string;
}

interface AssignableUsersResponse {
  clients: AssignableUser[];
  projectManagers: AssignableUser[];
  developers: AssignableUser[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminProjectDetailsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [processingPhase, setProcessingPhase] = useState<string | null>(null);
  const [discoveryInterview, setDiscoveryInterview] = useState<DiscoveryInterviewPayload | null>(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [discoveryTab, setDiscoveryTab] = useState<'summary' | 'questions' | 'transcript' | 'actions'>('summary');
  const [transcriptFilter, setTranscriptFilter] = useState<'all' | 'ai' | 'client'>('all');
  const [interviewWorkspaceOpen, setInterviewWorkspaceOpen] = useState(false);
  const [requestingFollowUps, setRequestingFollowUps] = useState(false);
  const [completingInterview, setCompletingInterview] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUsersResponse | null>(null);
  const [availableDevelopers, setAvailableDevelopers] = useState<AssignmentDeveloper[]>([]);
  const [teamDataLoading, setTeamDataLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [assignPmDialogOpen, setAssignPmDialogOpen] = useState(false);
  const [selectedProjectManagerId, setSelectedProjectManagerId] = useState<number | null>(null);
  const [assigningProjectManager, setAssigningProjectManager] = useState(false);
  const [assignDevelopersDialogOpen, setAssignDevelopersDialogOpen] = useState(false);
  const [selectedDeveloperIds, setSelectedDeveloperIds] = useState<number[]>([]);
  const [assigningDevelopers, setAssigningDevelopers] = useState(false);
  const [notifyDevelopers, setNotifyDevelopers] = useState(true);
  const [removeDeveloperId, setRemoveDeveloperId] = useState<number | null>(null);
  const [removingDeveloperId, setRemovingDeveloperId] = useState<number | null>(null);
  
  // Status transition state
  const [allowedStatusTransitions, setAllowedStatusTransitions] = useState<string[]>([]);
  const [statusTransitionDialogOpen, setStatusTransitionDialogOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState<string>('');
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  const specKitService = new SpecKitDockerService();

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  useEffect(() => {
    if (discoveryInterview?.interview?.id) {
      setDiscoveryTab('summary');
      setTranscriptFilter('all');
    }
  }, [discoveryInterview?.interview?.id]);

  useEffect(() => {
    if (project?.projectManager?.id) {
      setSelectedProjectManagerId(project.projectManager.id);
    } else {
      setSelectedProjectManagerId(null);
    }
  }, [project?.projectManager?.id]);

  const loadTeamData = async (id: number) => {
    try {
      setTeamDataLoading(true);
      setTeamError(null);

      const [assignableResponse, developersResponse] = await Promise.all([
        ApiService.get<{ success: boolean; data: AssignableUsersResponse; message?: string }>('/admin/projects/assignable-users'),
        ProjectAssignmentService.getAvailableDevelopers(),
      ]);

      if (assignableResponse.success) {
        setAssignableUsers(assignableResponse.data);
      } else {
        throw new Error(assignableResponse.message || 'Failed to load assignable users');
      }

      setAvailableDevelopers(developersResponse);
    } catch (error) {
      console.error('Failed to load team data:', error);
      setTeamError('Unable to load team data right now.');
    } finally {
      setTeamDataLoading(false);
    }
  };

  const fetchProjectDetails = async () => {
    if (!projectId) {
      setProject(null);
      setDiscoveryInterview(null);
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.get<{ success: boolean; data: Project }>(`/admin/projects/${projectId}`);
      setProject(response.data);
      fetchDiscoveryInterviewDetails(response.data.id);
      await loadTeamData(response.data.id);
      await fetchAllowedStatusTransitions(response.data.id);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      showError('Failed to load project details');
      setDiscoveryInterview(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllowedStatusTransitions = async (id: number) => {
    try {
      const response = await ApiService.get<{ success: boolean; data: { allowed_transitions: string[] } }>(
        `/admin/projects/${id}/status/transitions`
      );
      if (response.success) {
        setAllowedStatusTransitions(response.data.allowed_transitions);
      }
    } catch (error) {
      console.error('Failed to fetch allowed status transitions:', error);
    }
  };

  const handleOpenStatusDialog = () => {
    setSelectedNewStatus('');
    setStatusChangeReason('');
    setStatusTransitionDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusTransitionDialogOpen(false);
    setSelectedNewStatus('');
    setStatusChangeReason('');
  };

  const handleStatusChange = async () => {
    if (!project || !selectedNewStatus) return;

    try {
      setUpdatingStatus(true);
      const response = await ApiService.put<{ success: boolean; data: any }>(
        `/admin/projects/${project.id}/status`,
        {
          status: selectedNewStatus,
          reason: statusChangeReason || undefined,
        }
      );

      if (response.success) {
        showSuccess(`Project status updated to ${selectedNewStatus.replace('_', ' ')}`);
        handleCloseStatusDialog();
        await fetchProjectDetails();
      }
    } catch (error: any) {
      console.error('Failed to update project status:', error);
      showError(error?.response?.data?.message || 'Failed to update project status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const fetchDiscoveryInterviewDetails = async (id: number) => {
    try {
      setDiscoveryLoading(true);
      setDiscoveryError(null);

      const response = await ApiService.get<{ success: boolean; data: DiscoveryInterviewPayload }>(
        `/admin/projects/${id}/discovery-interview`
      );

      if (response.success && response.data) {
        setDiscoveryInterview(response.data);
      } else {
        setDiscoveryInterview(null);
      }
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 404) {
        setDiscoveryInterview(null);
        setDiscoveryError(null);
      } else {
        console.error('Failed to load discovery interview details:', error);
        setDiscoveryInterview(null);
        setDiscoveryError('Failed to load discovery interview details');
      }
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const refreshProjectSnapshot = async (id: number) => {
    try {
      const response = await ApiService.get<{ success: boolean; data: Project }>(`/admin/projects/${id}`);
      if (response.success) {
        setProject(response.data);
      } else {
        throw new Error('Failed to refresh project details');
      }
    } catch (error) {
      console.error('Failed to refresh project snapshot:', error);
      showError('Unable to refresh project details');
    }
  };

  const handleDiscoveryTabChange = (_: React.SyntheticEvent, newValue: typeof discoveryTab) => {
    setDiscoveryTab(newValue);
  };

  const handleTranscriptFilterChange = (_: React.SyntheticEvent, newFilter: 'all' | 'ai' | 'client' | null) => {
    if (newFilter) {
      setTranscriptFilter(newFilter);
    }
  };

  const handleOpenInterviewWorkspace = () => {
    setInterviewWorkspaceOpen(true);
  };

  const handleCloseInterviewWorkspace = () => {
    setInterviewWorkspaceOpen(false);
  };

  const handleCopyTranscript = async () => {
    const transcript = discoveryInterview?.transcript ?? [];

    if (!transcript.length) {
      showInfo('No transcript available to copy');
      return;
    }

    const transcriptText = transcript
      .map((entry) => `${entry.role === 'ai' ? 'AI' : 'Client'} [${formatDateTime(entry.timestamp)}]: ${entry.message}`)
      .join('\n');

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(transcriptText);
        showSuccess('Discovery transcript copied to clipboard');
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (error) {
      console.error('Failed to copy transcript:', error);
      showError('Unable to copy transcript');
    }
  };

  const handleGenerateFollowUps = async () => {
    if (!project) return;

    try {
      setRequestingFollowUps(true);
      await ApiService.post(`/admin/projects/${project.id}/discovery-interview/generate-follow-ups`);
      showSuccess('Requested follow-up questions');
      await fetchDiscoveryInterviewDetails(project.id);
      setDiscoveryTab('questions');
    } catch (error) {
      console.error('Failed to request follow-ups:', error);
      showError('Could not request follow-up questions');
    } finally {
      setRequestingFollowUps(false);
    }
  };

  const handleCompleteInterview = async () => {
    if (!project) return;

    try {
      setCompletingInterview(true);
      await ApiService.post(`/admin/projects/${project.id}/discovery-interview/complete`);
      showSuccess('Marked discovery interview complete');
      await fetchDiscoveryInterviewDetails(project.id);
    } catch (error) {
      console.error('Failed to mark interview complete:', error);
      showError('Could not mark interview complete');
    } finally {
      setCompletingInterview(false);
    }
  };

  const handleRefreshInterview = () => {
    if (!project) return;
    showInfo('Refreshing discovery interview details');
    fetchDiscoveryInterviewDetails(project.id);
  };

  const handleOpenAssignPmDialog = () => {
    setSelectedProjectManagerId(project?.projectManager?.id ?? null);
    setAssignPmDialogOpen(true);
  };

  const handleCloseAssignPmDialog = () => {
    setSelectedProjectManagerId(project?.projectManager?.id ?? null);
    setAssignPmDialogOpen(false);
  };

  const handleAssignProjectManager = async () => {
    if (!project || !selectedProjectManagerId) {
      showError('Please select a project manager');
      return;
    }

    try {
      setAssigningProjectManager(true);
      const response = await ApiService.put<{ success: boolean; message?: string }>(
        `/admin/projects/${project.id}`,
        { project_manager_id: selectedProjectManagerId }
      );

      if (response.success) {
        showSuccess(response.message || 'Project manager updated');
        setAssignPmDialogOpen(false);
        await Promise.all([
          refreshProjectSnapshot(project.id),
          loadTeamData(project.id),
        ]);
      } else {
        showError(response.message || 'Failed to assign project manager');
      }
    } catch (error) {
      console.error('Failed to assign project manager:', error);
      showError('Could not assign project manager');
    } finally {
      setAssigningProjectManager(false);
    }
  };

  const handleOpenAssignDevelopersDialog = () => {
    setSelectedDeveloperIds([]);
    setNotifyDevelopers(true);
    setAssignDevelopersDialogOpen(true);
  };

  const handleCloseAssignDevelopersDialog = () => {
    setAssignDevelopersDialogOpen(false);
    setSelectedDeveloperIds([]);
  };

  const handleAssignDevelopers = async () => {
    if (!project) {
      showError('Project not available');
      return;
    }

    if (!selectedDeveloperIds.length) {
      showError('Select at least one developer to assign');
      return;
    }

    try {
      setAssigningDevelopers(true);
      const response = await ProjectAssignmentService.assignDevelopers(
        project.id,
        selectedDeveloperIds,
        notifyDevelopers
      );

      if (response.success) {
        showSuccess(response.message || 'Developers assigned successfully');
        setAssignDevelopersDialogOpen(false);
        await Promise.all([
          refreshProjectSnapshot(project.id),
          loadTeamData(project.id),
        ]);
      } else {
        showError(response.message || 'Failed to assign developers');
      }
    } catch (error) {
      console.error('Failed to assign developers:', error);
      showError('Could not assign selected developers');
    } finally {
      setAssigningDevelopers(false);
    }
  };

  const handleRemoveDeveloperClick = (developerId: number) => {
    setRemoveDeveloperId(developerId);
  };

  const handleCloseRemoveDeveloperDialog = () => {
    setRemoveDeveloperId(null);
  };

  const handleConfirmRemoveDeveloper = async () => {
    if (!project || removeDeveloperId === null) {
      return;
    }

    try {
      setRemovingDeveloperId(removeDeveloperId);
      const response = await ProjectAssignmentService.removeDeveloper(project.id, removeDeveloperId);

      if (response.success) {
        showSuccess(response.message || 'Developer removed');
        setRemoveDeveloperId(null);
        await Promise.all([
          refreshProjectSnapshot(project.id),
          loadTeamData(project.id),
        ]);
      } else {
        showError(response.message || 'Failed to remove developer');
      }
    } catch (error) {
      console.error('Failed to remove developer:', error);
      showError('Could not remove developer from project');
    } finally {
      setRemovingDeveloperId(null);
    }
  };

  const pollForStatus = async (phase: 'specification' | 'plan' | 'tasks', maxAttempts = 180) => {
    let attempts = 0;
    const pollInterval = 2000; // Check every 2 seconds (180 attempts = 6 minutes)

    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          attempts++;
          
          const response = await ApiService.get<{ success: boolean; data: any }>(
            `/admin/projects/sdd-workflow/${project?.id}/status`
          );

          if (response.success && response.data[phase]) {
            const phaseData = response.data[phase];
            
            if (phaseData.status === 'completed') {
              clearInterval(interval);
              await fetchProjectDetails();
              resolve();
            } else if (phaseData.status === 'failed') {
              clearInterval(interval);
              reject(new Error(phaseData.error || `${phase} generation failed`));
            }
            // If still pending or processing, continue polling
          }

          if (attempts >= maxAttempts) {
            clearInterval(interval);
            reject(new Error(`${phase} generation timed out after ${maxAttempts * pollInterval / 1000} seconds`));
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, pollInterval);
    });
  };

  const handleGenerateSpecification = async () => {
    if (!project) return;
    
    try {
      setProcessingPhase('specification');
      showInfo('Generating feature specification with AI (this may take 30-60 seconds)...');

      // Start the generation (returns immediately)
      const response = await ApiService.post<{ success: boolean; data: any }>(
        `/admin/projects/sdd-workflow/${project.id}/specification`,
        {
          feature_description: `${project.name}: ${project.description || ''}`,
          context: `Priority: ${project.priority}, Client: ${project.client?.name || 'N/A'}`,
        }
      );

      if (response.success) {
        // Poll for completion
        await pollForStatus('specification');
        showSuccess('Feature specification generated successfully!');
      } else {
        throw new Error('Failed to start specification generation');
      }
    } catch (error) {
      console.error('Specification generation failed:', error);
      showError(error instanceof Error ? error.message : 'Failed to generate specification');
    } finally {
      setProcessingPhase(null);
    }
  };

  const handleGeneratePlan = async () => {
    if (!project) return;
    
    try {
      setProcessingPhase('plan');
      showInfo('Generating technical implementation plan with AI (this may take 30-60 seconds)...');

      // Start the generation (returns immediately)
      const response = await ApiService.post<{ success: boolean; data: any }>(
        `/admin/projects/sdd-workflow/${project.id}/plan`,
        {
          technical_stack: {
            frontend: 'React 18',
            backend: 'Laravel 10',
            database: 'MySQL',
          },
        }
      );

      if (response.success) {
        // Poll for completion
        await pollForStatus('plan');
        showSuccess('Technical plan generated successfully!');
      } else {
        throw new Error('Failed to start plan generation');
      }
    } catch (error) {
      console.error('Plan generation failed:', error);
      showError(error instanceof Error ? error.message : 'Failed to generate plan');
    } finally {
      setProcessingPhase(null);
    }
  };

  const handleGenerateTasks = async () => {
    if (!project) return;
    
    try {
      setProcessingPhase('tasks');
      showInfo('Generating task breakdown with AI (this may take 30-60 seconds)...');

      // Start the generation (returns immediately)
      const response = await ApiService.post<{ success: boolean; data: any }>(
        `/admin/projects/sdd-workflow/${project.id}/tasks`,
        {
          team_size: (project.developers?.length || 0) + 1, // +1 for PM
          complexity: project.priority === 'urgent' || project.priority === 'high' ? 'complex' : 'medium',
        }
      );

      if (response.success) {
        // Poll for completion
        await pollForStatus('tasks');
        showSuccess('Task breakdown generated successfully!');
      } else {
        throw new Error('Failed to start task generation');
      }
    } catch (error) {
      console.error('Task generation failed:', error);
      showError(error instanceof Error ? error.message : 'Failed to generate tasks');
    } finally {
      setProcessingPhase(null);
    }
  };

  const handleCreateTasks = async () => {
    if (!project) return;
    
    try {
      setProcessingPhase('create_tasks');
      showInfo('Creating tasks from SDD workflow...');

      const response = await ApiService.post<{ success: boolean; data: any; message: string }>(
        `/admin/projects/sdd-workflow/${project.id}/create-tasks`
      );

      if (response.success) {
        showSuccess(response.message || 'Tasks created successfully!');
        await fetchProjectDetails();
      } else {
        throw new Error('Task creation failed');
      }
    } catch (error) {
      console.error('Task creation failed:', error);
      showError('Failed to create tasks');
    } finally {
      setProcessingPhase(null);
    }
  };

  const handleCreateGitHubRepo = async () => {
    if (!project) return;
    
    try {
      setProcessingPhase('github');
      showInfo('Creating GitHub repository and committing artifacts...');

      const response = await ApiService.post<{ success: boolean; data: any; message: string }>(
        `/admin/projects/sdd-workflow/${project.id}/create-repository`
      );

      if (response.success) {
        showSuccess(response.message || 'GitHub repository created successfully!');
        await fetchProjectDetails();
      } else {
        throw new Error('GitHub repository creation failed');
      }
    } catch (error) {
      console.error('GitHub repository creation failed:', error);
      showError('Failed to create GitHub repository');
    } finally {
      setProcessingPhase(null);
    }
  };

  const handlePushSpecsToRepo = async () => {
    if (!project) return;
    
    try {
      setProcessingPhase('github');
      showInfo('Pushing spec files to GitHub repository...');

      const response = await ApiService.post<{ success: boolean; data: any; message: string }>(
        `/admin/projects/sdd-workflow/${project.id}/push-specs`
      );

      if (response.success) {
        showSuccess(response.message || 'Spec files pushed successfully!');
        await fetchProjectDetails();
      } else {
        throw new Error('Failed to push specs');
      }
    } catch (error) {
      console.error('Push specs failed:', error);
      showError('Failed to push spec files to repository');
    } finally {
      setProcessingPhase(null);
    }
  };

  const getPhaseStatus = (phase: SDDPhase): PhaseStatus => {
    const sddState = project?.project_metadata?.sdd_state;
    if (!sddState) return PhaseStatus.NOT_STARTED;

    switch (phase) {
      case SDDPhase.CONSTITUTION:
        return sddState.constitution?.status || PhaseStatus.NOT_STARTED;
      case SDDPhase.SPECIFY:
        return sddState.specification?.status || PhaseStatus.NOT_STARTED;
      case SDDPhase.PLAN:
        return sddState.plan?.status || PhaseStatus.NOT_STARTED;
      case SDDPhase.TASKS:
        return sddState.tasks?.status || PhaseStatus.NOT_STARTED;
      default:
        return PhaseStatus.NOT_STARTED;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
      case 'approved':
      case 'closed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'needs_clarification':
      case 'blocked':
      case 'cancelled':
      case 'abandoned':
        return 'error';
      case 'in_review':
      case 'submitted':
        return 'info';
      case 'draft':
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDateTime = (dateString?: string): string => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getAvailabilityChipColor = (
    availability?: AssignmentDeveloper['availability']
  ): 'success' | 'warning' | 'error' | 'default' => {
    switch (availability) {
      case 'available':
        return 'success';
      case 'limited':
        return 'warning';
      case 'busy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPivotStatusColor = (
    status?: string
  ): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status) {
      case 'accepted':
        return 'success';
      case 'pending':
        return 'warning';
      case 'declined':
      case 'removed':
        return 'error';
      case 'notified':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatStatusLabel = (status?: string): string => {
    if (!status) return 'N/A';
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const interview = discoveryInterview?.interview;
  const isInterviewCompleted = interview?.status === 'completed';
  const answeredQuestions = discoveryInterview?.answered_questions ?? [];
  const unansweredQuestions = discoveryInterview?.unanswered_questions ?? [];
  const discoveryTranscript = discoveryInterview?.transcript ?? [];
  const recentTranscript = discoveryInterview?.transcript
    ? discoveryInterview.transcript.slice(-5)
    : [];
  const filteredTranscript = useMemo(() => {
    if (!discoveryTranscript.length) {
      return [];
    }

    if (transcriptFilter === 'all') {
      return discoveryTranscript;
    }

    return discoveryTranscript.filter((entry) => entry.role === transcriptFilter);
  }, [discoveryTranscript, transcriptFilter]);

  const developerAvailabilityMap = useMemo(() => {
    const map = new Map<number, AssignmentDeveloper>();
    availableDevelopers.forEach((dev) => {
      map.set(dev.id, dev);
    });
    return map;
  }, [availableDevelopers]);

  const assignedDeveloperIds = useMemo(() => {
    if (!project?.developers?.length) {
      return new Set<number>();
    }
    return new Set(project.developers.map((dev) => dev.id));
  }, [project?.developers]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Project not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/dashboard/admin/projects')} sx={{ mt: 2 }}>
          Back to Projects
        </Button>
      </Box>
    );
  }

  const selectedProjectManagerOption = assignableUsers?.projectManagers.find(
    (user) => user.id === selectedProjectManagerId
  ) ?? null;

  const selectedDeveloperOptions = availableDevelopers.filter((dev) =>
    selectedDeveloperIds.includes(dev.id)
  );

  const removalTarget = project.developers?.find((dev) => dev.id === removeDeveloperId) ?? null;

  const sddEnabled = project.project_metadata?.sdd_workflow_enabled;
  const sddState = project.project_metadata?.sdd_state;

  return (
    <>
      <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/dashboard/admin/projects')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              {project.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Project ID: {project.id} • Created {formatDate(project.created_at)}
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProjectDetails}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/dashboard/admin/projects/${project.id}/edit`)}
          >
            Edit Project
          </Button>
        </Box>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Status
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={project.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(project.status)}
                  size="small"
                />
                {allowedStatusTransitions.length > 0 && (
                  <Tooltip title="Change status">
                    <IconButton size="small" onClick={handleOpenStatusDialog}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Priority
              </Typography>
              <Chip
                label={project.priority.toUpperCase()}
                color={getPriorityColor(project.priority)}
                size="small"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Progress
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <SpeedIcon color="primary" />
                <Typography variant="h6">
                  {project.completion_percentage || 0}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Budget
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <MoneyIcon color="primary" />
                <Typography variant="h6">
                  {formatCurrency(project.budget_info?.amount)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Overview" />
          <Tab label="SDD Workflow" disabled={!sddEnabled} />
          <Tab label="Outputs & Artifacts" disabled={!sddEnabled} />
          <Tab label="GitHub Integration" />
          <Tab label="Team" />
          <Tab label="Timeline" />
          <Tab label="Tasks" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Project Description
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {project.description || 'No description provided'}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="h6" gutterBottom>
                    Project Configuration
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color={sddEnabled ? 'success' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary="SDD Workflow"
                        secondary={sddEnabled ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color={project.project_metadata?.speckit_integration_enabled ? 'success' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary="SpecKit Integration"
                        secondary={project.project_metadata?.speckit_integration_enabled ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color={project.project_metadata?.client_guided_workflow ? 'success' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Client Guided Workflow"
                        secondary={project.project_metadata?.client_guided_workflow ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color={project.project_metadata?.require_client_approval ? 'success' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Require Client Approval"
                        secondary={project.project_metadata?.require_client_approval ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Client
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon color="primary" />
                    <Box>
                      <Typography variant="body1">
                        {project.client?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {project.client?.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Project Manager
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon color="primary" />
                    <Box>
                      <Typography variant="body1">
                        {project.projectManager?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {project.projectManager?.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Timeline
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Start Date"
                        secondary={formatDate(project.expected_start_date)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="End Date"
                        secondary={formatDate(project.expected_end_date)}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* SDD Workflow Tab */}
        <TabPanel value={tabValue} index={1}>
          {sddEnabled ? (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                This project follows the Spec-Driven Development (SDD) workflow. Each phase must be completed before moving to the next.
              </Alert>

              <Stepper orientation="vertical">
                {/* Constitution Phase */}
                <Step active completed={getPhaseStatus(SDDPhase.CONSTITUTION) === PhaseStatus.APPROVED}>
                  <StepLabel
                    icon={getPhaseStatus(SDDPhase.CONSTITUTION) === PhaseStatus.APPROVED ? <CheckCircleIcon /> : <UncheckedIcon />}
                  >
                    <Typography variant="h6">Constitution Validation</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" paragraph>
                      Validate project against VegaDigital Constitution principles:
                      Code Quality, TDD, UX Consistency, Performance, Spec-Driven Development
                    </Typography>
                    <Chip
                      label={getPhaseStatus(SDDPhase.CONSTITUTION).replace('_', ' ').toUpperCase()}
                      color={getStatusColor(getPhaseStatus(SDDPhase.CONSTITUTION))}
                      size="small"
                    />
                    {sddState?.constitution?.approvedAt && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Approved on {formatDate(sddState.constitution.approvedAt)}
                      </Typography>
                    )}
                  </StepContent>
                </Step>

                {/* Specification Phase */}
                <Step active completed={getPhaseStatus(SDDPhase.SPECIFY) === PhaseStatus.COMPLETED}>
                  <StepLabel
                    icon={getPhaseStatus(SDDPhase.SPECIFY) === PhaseStatus.COMPLETED ? <CheckCircleIcon /> : <UncheckedIcon />}
                  >
                    <Typography variant="h6">Feature Specification</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" paragraph>
                      Generate detailed technical specification with user stories, functional requirements,
                      and success criteria.
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <Chip
                        label={getPhaseStatus(SDDPhase.SPECIFY).replace('_', ' ').toUpperCase()}
                        color={getStatusColor(getPhaseStatus(SDDPhase.SPECIFY))}
                        size="small"
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={processingPhase === 'specification' ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={handleGenerateSpecification}
                        disabled={processingPhase === 'specification' || getPhaseStatus(SDDPhase.SPECIFY) === PhaseStatus.COMPLETED}
                      >
                        Generate Specification
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                {/* Planning Phase */}
                <Step active completed={getPhaseStatus(SDDPhase.PLAN) === PhaseStatus.COMPLETED}>
                  <StepLabel
                    icon={getPhaseStatus(SDDPhase.PLAN) === PhaseStatus.COMPLETED ? <CheckCircleIcon /> : <UncheckedIcon />}
                  >
                    <Typography variant="h6">Technical Planning</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" paragraph>
                      Create implementation plan with technical architecture, data models, API contracts,
                      and performance targets.
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <Chip
                        label={getPhaseStatus(SDDPhase.PLAN).replace('_', ' ').toUpperCase()}
                        color={getStatusColor(getPhaseStatus(SDDPhase.PLAN))}
                        size="small"
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={processingPhase === 'plan' ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={handleGeneratePlan}
                        disabled={
                          processingPhase === 'plan' ||
                          getPhaseStatus(SDDPhase.PLAN) === PhaseStatus.COMPLETED ||
                          getPhaseStatus(SDDPhase.SPECIFY) !== PhaseStatus.COMPLETED
                        }
                      >
                        Generate Plan
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                {/* Tasks Phase */}
                <Step active completed={getPhaseStatus(SDDPhase.TASKS) === PhaseStatus.COMPLETED}>
                  <StepLabel
                    icon={getPhaseStatus(SDDPhase.TASKS) === PhaseStatus.COMPLETED ? <CheckCircleIcon /> : <UncheckedIcon />}
                  >
                    <Typography variant="h6">Task Breakdown</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" paragraph>
                      Break down the plan into actionable development tasks with estimates, dependencies,
                      and priorities.
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <Chip
                        label={getPhaseStatus(SDDPhase.TASKS).replace('_', ' ').toUpperCase()}
                        color={getStatusColor(getPhaseStatus(SDDPhase.TASKS))}
                        size="small"
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={processingPhase === 'tasks' ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={handleGenerateTasks}
                        disabled={
                          processingPhase === 'tasks' ||
                          getPhaseStatus(SDDPhase.TASKS) === PhaseStatus.COMPLETED ||
                          getPhaseStatus(SDDPhase.PLAN) !== PhaseStatus.COMPLETED
                        }
                      >
                        Generate Tasks
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                {/* Implementation Phase */}
                <Step active={sddState?.readyForImplementation}>
                  <StepLabel
                    icon={sddState?.readyForImplementation ? <CheckCircleIcon /> : <UncheckedIcon />}
                  >
                    <Typography variant="h6">Implementation</Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" paragraph>
                      Ready to begin development. Tasks can be assigned to developers and work can commence
                      following TDD practices.
                    </Typography>
                    {sddState?.readyForImplementation ? (
                      <Chip label="READY FOR IMPLEMENTATION" color="success" />
                    ) : (
                      <Chip label="WAITING FOR TASK BREAKDOWN" color="default" />
                    )}
                  </StepContent>
                </Step>
              </Stepper>
            </Box>
          ) : (
            <Alert severity="warning">
              SDD Workflow is not enabled for this project.
            </Alert>
          )}
        </TabPanel>

        {/* Outputs & Artifacts Tab */}
        <TabPanel value={tabValue} index={2}>
          {sddEnabled && (project.project_metadata?.sdd_outputs || project.project_metadata?.sdd_state) ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                SDD Workflow Outputs
              </Typography>
              
              {/* Specification Output - Check both sdd_outputs and sdd_state */}
              {(project.project_metadata.sdd_outputs?.specification || project.project_metadata.sdd_state?.specification) && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📋 Specification
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Generated: {new Date(
                        project.project_metadata.sdd_outputs?.specification?.executed_at ||
                        project.project_metadata.sdd_state?.specification?.createdAt ||
                        project.project_metadata.sdd_state?.specification?.updatedAt
                      ).toLocaleString()}
                    </Typography>
                    {project.project_metadata.sdd_state?.specification?.status && (
                      <Chip label={project.project_metadata.sdd_state.specification.status} color="success" size="small" sx={{ mb: 1 }} />
                    )}
                    <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {project.project_metadata.sdd_outputs?.specification?.output || 
                         JSON.stringify(project.project_metadata.sdd_state?.specification, null, 2)}
                      </pre>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Plan Output - Check both sdd_outputs and sdd_state */}
              {(project.project_metadata.sdd_outputs?.plan || project.project_metadata.sdd_state?.plan) && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      🏗️ Technical Plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Generated: {new Date(
                        project.project_metadata.sdd_outputs?.plan?.executed_at ||
                        project.project_metadata.sdd_state?.plan?.createdAt ||
                        project.project_metadata.sdd_state?.plan?.updatedAt
                      ).toLocaleString()}
                    </Typography>
                    {project.project_metadata.sdd_state?.plan?.status && (
                      <Chip label={project.project_metadata.sdd_state.plan.status} color="success" size="small" sx={{ mb: 1 }} />
                    )}
                    <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {project.project_metadata.sdd_outputs?.plan?.output || 
                         JSON.stringify(project.project_metadata.sdd_state?.plan, null, 2)}
                      </pre>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Tasks Output - Check both sdd_outputs and sdd_state */}
              {(project.project_metadata.sdd_outputs?.tasks || project.project_metadata.sdd_state?.tasks) && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          ✅ Task Breakdown
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Generated: {new Date(
                            project.project_metadata.sdd_outputs?.tasks?.executed_at ||
                            project.project_metadata.sdd_state?.tasks?.createdAt ||
                            project.project_metadata.sdd_state?.tasks?.updatedAt
                          ).toLocaleString()}
                        </Typography>
                        {project.project_metadata.sdd_state?.tasks?.status && (
                          <Chip label={project.project_metadata.sdd_state.tasks.status} color="success" size="small" sx={{ mt: 0.5 }} />
                        )}
                      </Box>
                      <Box display="flex" gap={1}>
                        {/* Show Create Tasks button if tasks haven't been created yet */}
                        {!project.project_metadata.sdd_outputs?.tasks?.tasks_created && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateTasks}
                            disabled={processingPhase === 'create_tasks'}
                            startIcon={processingPhase === 'create_tasks' ? <CircularProgress size={16} /> : undefined}
                          >
                            Create Tasks from Breakdown
                          </Button>
                        )}
                        
                        {/* Show task count if tasks were created successfully */}
                        {project.project_metadata.sdd_outputs?.tasks?.tasks_created && 
                         project.project_metadata.sdd_outputs?.tasks?.tasks_count > 0 && (
                          <Chip
                            label={`${project.project_metadata.sdd_outputs.tasks.tasks_count} tasks created`}
                            color="success"
                            icon={<CheckCircleIcon />}
                          />
                        )}
                        
                        {/* Show warning and regenerate button if 0 tasks were created */}
                        {project.project_metadata.sdd_outputs?.tasks?.tasks_created && 
                         project.project_metadata.sdd_outputs?.tasks?.tasks_count === 0 && (
                          <>
                            <Chip
                              label="0 tasks created"
                              color="warning"
                              icon={<WarningIcon />}
                            />
                            <Button
                              variant="outlined"
                              color="warning"
                              onClick={handleGenerateTasks}
                              disabled={processingPhase === 'tasks'}
                              startIcon={processingPhase === 'tasks' ? <CircularProgress size={16} /> : undefined}
                            >
                              Regenerate Tasks
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>

                    <Card sx={{ mt: 3 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6">Discovery Interview</Typography>
                          {discoveryInterview?.interview?.status && (
                            <Chip
                              label={discoveryInterview.interview.status.replace('_', ' ').toUpperCase()}
                              color={getStatusColor(discoveryInterview.interview.status)}
                              size="small"
                            />
                          )}
                        </Box>

                        {discoveryLoading && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                              Loading discovery interview...
                            </Typography>
                          </Box>
                        )}

                        {discoveryError && (
                          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDiscoveryError(null)}>
                            {discoveryError}
                          </Alert>
                        )}

                        {!discoveryLoading && !discoveryInterview && !discoveryError && (
                          <Typography variant="body2" color="text.secondary">
                            No discovery interview has been started for this project.
                          </Typography>
                        )}

                        {discoveryInterview && (
                          <Box>
                            <Stack spacing={2}>
                              <Typography variant="body2" color="text.secondary">
                                Initial Idea:&nbsp;
                                <Typography component="span" variant="body2" color="text.primary">
                                  {interview?.initial_idea || 'Not provided yet'}
                                </Typography>
                              </Typography>

                              <Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={interview?.progress_percentage ?? 0}
                                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                                />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" color="text.secondary">
                                    {interview?.questions_answered ?? 0} of {interview?.questions_asked ?? 0} questions answered
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {(interview?.progress_percentage ?? 0).toFixed(0)}% complete
                                  </Typography>
                                </Stack>
                              </Box>

                              <Stack direction="row" spacing={2} flexWrap="wrap">
                                <Typography variant="body2" color="text.secondary">
                                  Started: <strong>{formatDateTime(interview?.started_at)}</strong>
                                </Typography>
                                {interview?.completed_at && (
                                  <Typography variant="body2" color="text.secondary">
                                    Completed: <strong>{formatDateTime(interview.completed_at)}</strong>
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>

                            <Tabs
                              value={discoveryTab}
                              onChange={handleDiscoveryTabChange}
                              sx={{ mt: 3, borderBottom: 1, borderColor: 'divider' }}
                              variant="scrollable"
                              allowScrollButtonsMobile
                            >
                              <Tab label="Summary" value="summary" />
                              <Tab label={`Questions (${discoveryInterview.questions.length})`} value="questions" />
                              <Tab label={`Transcript (${discoveryTranscript.length})`} value="transcript" />
                              <Tab label="Actions" value="actions" />
                            </Tabs>

                            {discoveryTab === 'summary' && (
                              <Box sx={{ mt: 3 }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                  <Chip label={`${answeredQuestions.length} answered`} color={answeredQuestions.length ? 'success' : 'default'} size="small" />
                                  <Chip label={`${unansweredQuestions.length} pending`} color={unansweredQuestions.length ? 'warning' : 'default'} size="small" />
                                  <Chip label={`${discoveryTranscript.length} transcript messages`} size="small" />
                                </Stack>

                                <Typography variant="subtitle2" gutterBottom>
                                  Key Answers
                                </Typography>
                                {answeredQuestions.length > 0 ? (
                                  <List dense>
                                    {answeredQuestions.slice(0, 4).map((question) => (
                                      <ListItem key={question.id} alignItems="flex-start" disableGutters sx={{ pb: 1.5 }}>
                                        <ListItemText
                                          primary={
                                            <Typography variant="body2" fontWeight={600}>
                                              {question.question}
                                            </Typography>
                                          }
                                          secondary={
                                            <>
                                              {question.answer && (
                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                  {question.answer}
                                                </Typography>
                                              )}
                                              {question.answered_at && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Answered {formatDateTime(question.answered_at)}
                                                </Typography>
                                              )}
                                            </>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Answers will appear here once the interview progresses.
                                  </Typography>
                                )}

                                {answeredQuestions.length > 4 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Switch to the Questions tab to explore the full answer set.
                                  </Typography>
                                )}

                                <Divider sx={{ my: 3 }} />

                                <Typography variant="subtitle2" gutterBottom>
                                  Recent Transcript
                                </Typography>
                                {recentTranscript.length > 0 ? (
                                  <Box>
                                    <List dense>
                                      {recentTranscript.map((entry, index) => (
                                        <ListItem key={`${entry.timestamp}-${index}`} alignItems="flex-start" disableGutters sx={{ pb: 1.5 }}>
                                          <ListItemText
                                            primary={
                                              <Typography variant="body2" fontWeight={500}>
                                                {entry.role === 'ai' ? 'AI' : 'Client'} • {formatDateTime(entry.timestamp)}
                                              </Typography>
                                            }
                                            secondary={
                                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                {entry.message}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                    {discoveryTranscript.length > recentTranscript.length && (
                                      <Typography variant="caption" color="text.secondary">
                                        Showing the 5 most recent messages.
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    Transcript entries will appear as the interview captures new dialogue.
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {discoveryTab === 'questions' && (
                              <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Unanswered Questions ({unansweredQuestions.length})
                                </Typography>
                                {unansweredQuestions.length > 0 ? (
                                  <List dense>
                                    {unansweredQuestions.map((question) => (
                                      <ListItem key={`unanswered-${question.id}`} alignItems="flex-start" sx={{ pb: 1.5 }}>
                                        <ListItemText
                                          primary={
                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                              <Typography variant="body2" fontWeight={600}>
                                                {question.question}
                                              </Typography>
                                              {question.category && <Chip label={question.category} size="small" />}
                                            </Stack>
                                          }
                                          secondary={
                                            <>
                                              {question.context && (
                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                  {question.context}
                                                </Typography>
                                              )}
                                              <Typography variant="caption" color="text.secondary">
                                                Awaiting client response
                                              </Typography>
                                            </>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    All discovery questions have been addressed.
                                  </Typography>
                                )}

                                <Divider sx={{ my: 3 }} />

                                <Typography variant="subtitle2" gutterBottom>
                                  Answered Questions ({answeredQuestions.length})
                                </Typography>
                                {answeredQuestions.length > 0 ? (
                                  <List dense>
                                    {answeredQuestions.map((question) => (
                                      <ListItem key={`answered-${question.id}`} alignItems="flex-start" sx={{ pb: 1.5 }}>
                                        <ListItemText
                                          primary={
                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                              <Typography variant="body2" fontWeight={600}>
                                                {question.question}
                                              </Typography>
                                              {question.category && <Chip label={question.category} size="small" color="success" />}
                                            </Stack>
                                          }
                                          secondary={
                                            <>
                                              {question.answer && (
                                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                  {question.answer}
                                                </Typography>
                                              )}
                                              {question.answered_at && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Answered {formatDateTime(question.answered_at)}
                                                </Typography>
                                              )}
                                            </>
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No answers captured yet.
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {discoveryTab === 'transcript' && (
                              <Box sx={{ mt: 3 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                                  <ToggleButtonGroup
                                    value={transcriptFilter}
                                    exclusive
                                    onChange={handleTranscriptFilterChange}
                                    size="small"
                                  >
                                    <ToggleButton value="all">All</ToggleButton>
                                    <ToggleButton value="client">Client</ToggleButton>
                                    <ToggleButton value="ai">AI</ToggleButton>
                                  </ToggleButtonGroup>

                                  <Button
                                    variant="outlined"
                                    startIcon={<CopyIcon />}
                                    onClick={handleCopyTranscript}
                                    disabled={!discoveryTranscript.length}
                                  >
                                    Copy Transcript
                                  </Button>
                                </Stack>

                                <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
                                  {filteredTranscript.length > 0 ? (
                                    <List dense>
                                      {filteredTranscript.map((entry, index) => (
                                        <ListItem key={`transcript-${entry.timestamp}-${index}`} alignItems="flex-start" sx={{ pb: 1.5 }}>
                                          <ListItemText
                                            primary={
                                              <Typography variant="body2" fontWeight={500}>
                                                {entry.role === 'ai' ? 'AI' : 'Client'} • {formatDateTime(entry.timestamp)}
                                              </Typography>
                                            }
                                            secondary={
                                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                {entry.message}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No transcript entries match the selected filter yet.
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            )}

                            {discoveryTab === 'actions' && (
                              <Box sx={{ mt: 3 }}>
                                <Stack spacing={2}>
                                  {!isInterviewCompleted && unansweredQuestions.length > 0 && (
                                    <Alert severity="info">
                                      {unansweredQuestions.length} question{unansweredQuestions.length === 1 ? '' : 's'} still need attention.
                                    </Alert>
                                  )}
                                  {isInterviewCompleted && (
                                    <Alert severity="success">This interview is marked complete.</Alert>
                                  )}

                                  <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <Button
                                      variant="contained"
                                      startIcon={<ChatIcon />}
                                      onClick={handleOpenInterviewWorkspace}
                                    >
                                      Open Interview Workspace
                                    </Button>

                                    <Button
                                      variant="outlined"
                                      startIcon={requestingFollowUps ? <CircularProgress size={16} /> : <ForumIcon />}
                                      onClick={handleGenerateFollowUps}
                                      disabled={requestingFollowUps}
                                    >
                                      Request Follow-up Questions
                                    </Button>

                                    <Button
                                      variant="outlined"
                                      startIcon={<RefreshIcon />}
                                      onClick={handleRefreshInterview}
                                      disabled={discoveryLoading}
                                    >
                                      Refresh Interview Data
                                    </Button>

                                    <Button
                                      variant="contained"
                                      color="success"
                                      startIcon={completingInterview ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                      onClick={handleCompleteInterview}
                                      disabled={isInterviewCompleted || completingInterview}
                                    >
                                      Mark Interview Complete
                                    </Button>
                                  </Stack>
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                    <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {project.project_metadata.sdd_outputs?.tasks?.output || 
                         JSON.stringify(project.project_metadata.sdd_state?.tasks, null, 2)}
                      </pre>
                    </Box>
                  </CardContent>
                </Card>
              )}

          {/* GitHub Repository Actions */}
              {(project.project_metadata.sdd_outputs?.specification || project.project_metadata.sdd_state?.specification) && 
               (project.project_metadata.sdd_outputs?.plan || project.project_metadata.sdd_state?.plan) && 
               (project.project_metadata.sdd_outputs?.tasks || project.project_metadata.sdd_state?.tasks) && (
                <Card>
                  <CardContent>
                    {!project.project_metadata.github_repository ? (
                      <>
                        <Typography variant="h6" gutterBottom>
                          🚀 Create GitHub Repository
                        </Typography>
                        <Typography variant="body2" paragraph>
                          All SDD workflow phases are complete. Create a GitHub repository to store the project artifacts.
                        </Typography>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleCreateGitHubRepo}
                          disabled={processingPhase === 'github'}
                          startIcon={processingPhase === 'github' ? <CircularProgress size={16} /> : undefined}
                        >
                          Create GitHub Repository & Commit Files
                        </Button>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" gutterBottom>
                          📤 Push Specs to GitHub
                        </Typography>
                        <Typography variant="body2" paragraph>
                          Repository: <strong>{project.project_metadata.github_repository.full_name}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Update or push spec files to the repository (specification.md, plan.md, tasks.json)
                        </Typography>
                        <Box display="flex" gap={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePushSpecsToRepo}
                            disabled={processingPhase === 'github'}
                            startIcon={processingPhase === 'github' ? <CircularProgress size={16} /> : undefined}
                          >
                            Push/Update Spec Files
                          </Button>
                          <Button
                            variant="outlined"
                            href={project.project_metadata.github_repository.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Repository
                          </Button>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              No SDD workflow outputs available yet. Complete the workflow phases to see outputs here.
            </Alert>
          )}
        </TabPanel>

        {/* GitHub Integration Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box>
            <Typography variant="h5" gutterBottom>
              GitHub Integration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Connect and manage GitHub repositories for this project. Import repositories to enable automatic syncing and webhook integration.
            </Typography>
            
            <GitHubImport projectId={project.id} />

            {/* Legacy GitHub Repository Info (if exists in metadata) */}
            {project.project_metadata?.github_repository && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 3 }} />
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Legacy Repository Info
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      This project has a repository created through the old workflow system.
                    </Typography>
                    
                    {/* Repository Quick Links */}
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          href={`https://github.com/${project.project_metadata.github_repository.full_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<CodeIcon />}
                        >
                          View Code
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          href={`https://github.com/${project.project_metadata.github_repository.full_name}/tree/main/specs`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<DescriptionIcon />}
                        >
                          View Specs
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          href={`https://github.com/${project.project_metadata.github_repository.full_name}/commits/main`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<RefreshIcon />}
                        >
                          View Commits
                        </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          href={`https://github.com/${project.project_metadata.github_repository.full_name}/issues`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<TaskIcon />}
                        >
                          View Issues
                        </Button>
                      </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        href={`https://github.com/${project.project_metadata.github_repository.full_name}/pulls`}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<CodeIcon />}
                      >
                        View Pull Requests
                      </Button>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          href={`https://github.com/${project.project_metadata.github_repository.full_name}/settings`}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<EditIcon />}
                        >
                          Repository Settings
                        </Button>
                      </Grid>
                    </Grid>
                    
                    <Alert severity="info" sx={{ mt: 2 }}>
                      GitHub doesn't allow embedding their pages in iframes for security reasons. 
                      Use the links above to navigate to different sections of your repository.
                    </Alert>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Team Tab */}
        <TabPanel value={tabValue} index={4}>
          <Stack spacing={3}>
            {teamDataLoading && <LinearProgress />}
            {teamError && (
              <Alert severity="error" onClose={() => setTeamError(null)}>
                {teamError}
              </Alert>
            )}

            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Project Manager
                    </Typography>
                    {project.projectManager ? (
                      <Stack spacing={0.5}>
                        <Typography variant="body1">{project.projectManager.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {project.projectManager.email}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No project manager assigned yet.
                      </Typography>
                    )}
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<ManageAccountsIcon />}
                    onClick={handleOpenAssignPmDialog}
                  >
                    {project.projectManager ? 'Change Manager' : 'Assign Manager'}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Developers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage development assignments and workload visibility.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<GroupAddIcon />}
                    onClick={handleOpenAssignDevelopersDialog}
                    disabled={teamDataLoading}
                  >
                    Assign Developers
                  </Button>
                </Box>

                {project.developers && project.developers.length > 0 ? (
                  <List disablePadding>
                    {project.developers.map((dev) => {
                      const meta = developerAvailabilityMap.get(dev.id);
                      const activeProjectsCount = meta?.active_projects_count ?? 0;
                      const activeProjectsLabel = activeProjectsCount
                        ? `${activeProjectsCount} active project${activeProjectsCount === 1 ? '' : 's'}`
                        : null;

                      return (
                        <ListItem
                          key={dev.id}
                          alignItems="flex-start"
                          disableGutters
                          sx={{
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-of-type': { borderBottom: 'none' },
                          }}
                          secondaryAction={
                            <Stack direction="row" spacing={1} alignItems="center">
                              {meta?.availability && (
                                <Chip
                                  label={`Availability: ${meta.availability}`}
                                  color={getAvailabilityChipColor(meta.availability)}
                                  size="small"
                                />
                              )}
                              {dev.pivot?.status && (
                                <Chip
                                  label={formatStatusLabel(dev.pivot.status)}
                                  color={getPivotStatusColor(dev.pivot.status)}
                                  size="small"
                                />
                              )}
                              <Tooltip title="Remove developer">
                                <span>
                                  <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRemoveDeveloperClick(dev.id)}
                                    disabled={removingDeveloperId === dev.id}
                                  >
                                    {removingDeveloperId === dev.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <RemoveCircleIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar>{dev.name.charAt(0).toUpperCase()}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body1">{dev.name}</Typography>
                                {dev.pivot?.role && (
                                  <Chip
                                    label={dev.pivot.role.charAt(0).toUpperCase() + dev.pivot.role.slice(1)}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                {dev.pivot?.allocation_percentage && (
                                  <Chip
                                    label={`${dev.pivot.allocation_percentage}% allocated`}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {dev.email}
                                {activeProjectsLabel ? ` • ${activeProjectsLabel}` : ''}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Alert severity="info">
                    No developers assigned yet. Use the Assign Developers action to add team members.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Stack>
        </TabPanel>

        {/* Timeline Tab */}
        <TabPanel value={tabValue} index={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Project Timeline
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expected Start Date"
                    secondary={formatDate(project.expected_start_date)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Expected End Date"
                    secondary={formatDate(project.expected_end_date)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <SpeedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Current Progress"
                    secondary={`${project.completion_percentage || 0}% complete`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={tabValue} index={6}>
          {project.project_metadata?.sdd_outputs?.tasks?.tasks_created ? (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Project Tasks
                  </Typography>
                  <Chip 
                    label={`${project.project_metadata.sdd_outputs.tasks.tasks_count || 0} tasks`}
                    color="primary"
                  />
                </Box>
                
                <Alert severity="success" sx={{ mb: 2 }}>
                  {project.project_metadata.sdd_outputs.tasks.tasks_count || 0} tasks have been created from the SDD workflow breakdown.
                  Click the button below to view and manage all project tasks.
                </Alert>
                
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(`/dashboard/admin/projects/${project.id}/tasks`)}
                  startIcon={<TaskIcon />}
                >
                  Manage Tasks
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Tasks
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  No tasks have been created yet. Complete the SDD workflow to generate tasks, or create tasks manually.
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => navigate(`/dashboard/admin/projects/${project.id}/tasks`)}
                  startIcon={<TaskIcon />}
                >
                  Manage Tasks
                </Button>
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </Paper>
      </Box>

      <Dialog
        open={assignPmDialogOpen}
        onClose={handleCloseAssignPmDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign Project Manager</DialogTitle>
        <DialogContent dividers>
          {assignableUsers ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Select a project manager to oversee this engagement. This change takes effect immediately.
              </Typography>
              <Autocomplete<AssignableUser>
                options={assignableUsers.projectManagers || []}
                value={selectedProjectManagerOption}
                onChange={(_, value) => setSelectedProjectManagerId(value?.id ?? null)}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project Manager"
                    placeholder="Search project managers"
                  />
                )}
              />
            </Stack>
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignPmDialog} disabled={assigningProjectManager}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignProjectManager}
            disabled={assigningProjectManager || !selectedProjectManagerId}
          >
            {assigningProjectManager ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDevelopersDialogOpen}
        onClose={handleCloseAssignDevelopersDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Assign Developers</DialogTitle>
        <DialogContent dividers>
          {availableDevelopers.length > 0 ? (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Select developers to assign. Existing assignments are disabled to prevent duplicates.
              </Typography>
              <Autocomplete<AssignmentDeveloper, true, false, false>
                multiple
                options={availableDevelopers}
                value={selectedDeveloperOptions}
                onChange={(_, value) => setSelectedDeveloperIds(value.map((item) => item.id))}
                getOptionLabel={(option) => `${option.name} • ${option.email}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect
                getOptionDisabled={(option) => assignedDeveloperIds.has(option.id)}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box display="flex" flexDirection="column">
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email} • Availability: {option.availability}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Developers"
                    placeholder="Search developers"
                  />
                )}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notifyDevelopers}
                    onChange={(_, checked) => setNotifyDevelopers(checked)}
                    color="primary"
                  />
                }
                label="Notify developers about this assignment"
              />
            </Stack>
          ) : (
            <Alert severity="info">
              No available developers were found. Adjust workload or activate additional developers to assign them here.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDevelopersDialog} disabled={assigningDevelopers}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignDevelopers}
            disabled={assigningDevelopers || !selectedDeveloperIds.length}
          >
            {assigningDevelopers ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeDeveloperId !== null}
        onClose={handleCloseRemoveDeveloperDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Remove Developer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              {removalTarget
                ? `Remove ${removalTarget.name} from this project?`
                : 'Remove the selected developer from this project?'}
            </Typography>
            {removalTarget?.pivot?.status === 'pending' && (
              <Alert severity="warning">
                This developer has not accepted their assignment yet. Removing them will cancel the pending request.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRemoveDeveloperDialog}
            disabled={removingDeveloperId === removeDeveloperId}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRemoveDeveloper}
            disabled={removingDeveloperId === removeDeveloperId}
          >
            {removingDeveloperId === removeDeveloperId ? <CircularProgress size={18} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={interviewWorkspaceOpen}
        onClose={handleCloseInterviewWorkspace}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Discovery Interview Workspace</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {project && (
            <DiscoveryInterviewChat
              projectId={project.id}
              initialIdea={interview?.initial_idea || project.description || ''}
              onComplete={() => {
                if (project) {
                  fetchDiscoveryInterviewDetails(project.id);
                }
                handleCloseInterviewWorkspace();
                showSuccess('Discovery interview updated');
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInterviewWorkspace}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog
        open={statusTransitionDialogOpen}
        onClose={handleCloseStatusDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Change Project Status</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Status
              </Typography>
              <Chip
                label={project?.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(project?.status || '')}
                size="small"
              />
            </Box>

            <TextField
              select
              label="New Status"
              value={selectedNewStatus}
              onChange={(e) => setSelectedNewStatus(e.target.value)}
              fullWidth
              required
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select new status...</option>
              {allowedStatusTransitions.map((status) => (
                <option key={status} value={status}>
                  {status.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </TextField>

            {selectedNewStatus && (
              <Alert severity="info">
                {selectedNewStatus === 'in_progress' && 'This will mark the project as active and set the start date.'}
                {selectedNewStatus === 'completed' && 'This will mark the project as complete and set the end date.'}
                {selectedNewStatus === 'abandoned' && 'This will permanently abandon the project.'}
                {selectedNewStatus === 'cancelled' && 'This will permanently cancel the project.'}
              </Alert>
            )}

            <TextField
              label="Reason (optional)"
              value={statusChangeReason}
              onChange={(e) => setStatusChangeReason(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Explain why this status change is being made..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog} disabled={updatingStatus}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusChange}
            disabled={updatingStatus || !selectedNewStatus}
          >
            {updatingStatus ? <CircularProgress size={18} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminProjectDetailsPage;
